import React, { useMemo, useState } from "react";
import { Select, MenuItem, ListSubheader, TextField, InputAdornment, Tooltip } from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";

/**
 * Reusable DataGrid header filter dropdown with search.
 * Supports string options or `{ value, label }` objects.
 *
 * Hover label and filter-open are separate:
 * - isHovered  → mouse is over THIS header only
 * - menuOpen   → THIS header's dropdown is open
 * Never treat menuOpen as hover (that caused sticky multi-header labels).
 */
const FilterHeaderDropdown = ({ label, value, options = [], onChange }) => {
  const [searchText, setSearchText] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const normalizedOptions = useMemo(() => {
    const mapped = (options || []).map((option) =>
      option != null && typeof option === "object"
        ? { value: option.value, label: option.label ?? String(option.value) }
        : { value: option, label: String(option) }
    );
    return mapped.sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [options]);

  const selectedLabel = useMemo(() => {
    if (value === "" || value == null) return "";
    const match = normalizedOptions.find((opt) => opt.value === value || String(opt.value) === String(value));
    return match ? match.label : String(value);
  }, [normalizedOptions, value]);

  const displayText = selectedLabel || label;

  const filteredOptions = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return normalizedOptions;

    const matches = normalizedOptions.filter((opt) => String(opt.label).toLowerCase().includes(query));

    // Keep the current selection visible so MUI Select does not warn about an out-of-range value.
    if (value !== "" && value != null) {
      const selected = normalizedOptions.find((opt) => opt.value === value || String(opt.value) === String(value));
      if (selected && !matches.some((opt) => opt.value === selected.value || String(opt.value) === String(selected.value))) {
        return [selected, ...matches];
      }
    }
    return matches;
  }, [normalizedOptions, searchText, value]);

  const handleClose = () => {
    setMenuOpen(false);
    setSearchText("");
  };

  // Hover label only while the pointer is on this header and its menu is closed.
  // Native `title` on the label text (not MUI Tooltip / outer wrapper only):
  // Select children often capture hover, so the title must live on the visible label.
  const hoverTitle = isHovered && !menuOpen ? displayText : undefined;

  return (
    <span
      style={{ display: "block", width: "100%", maxWidth: "100%", minWidth: 0 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Select
        value={value ?? ""}
        open={menuOpen}
        onOpen={() => {
          setIsHovered(false);
          setMenuOpen(true);
        }}
        onChange={(e) => onChange(e.target.value)}
        onClose={handleClose}
        onMouseDown={() => setIsHovered(false)}
        displayEmpty
        title={hoverTitle}
        sx={{
          width: "100%",
          minWidth: 0,
          fontSize: "0.750rem",
          fontWeight: value ? "normal" : 600,
          // Theme sets OutlinedInput to solid white; stay transparent so the
          // dropdown matches whatever DataGrid header background the page uses.
          backgroundColor: "transparent",
          "& .MuiSelect-select": {
            backgroundColor: "transparent",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            pr: "24px !important",
          },
        }}
        renderValue={() => (
          <span
            title={hoverTitle}
            style={{
              fontSize: "0.750rem",
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {displayText}
          </span>
        )}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            style: { maxHeight: 320 },
          },
        }}
      >
        <ListSubheader sx={{ bgcolor: "background.paper", lineHeight: "normal", py: 1 }}>
          <TextField
            size="small"
            autoFocus
            fullWidth
            placeholder={`Search ${label}...`}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              // Keep typing inside the search field; don't let Select steal key events.
              if (e.key !== "Escape") e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              sx: { fontSize: "0.875rem" },
            }}
          />
        </ListSubheader>
        <MenuItem value="" style={{ fontWeight: 600 }}>
          {label}
        </MenuItem>
        {filteredOptions.length === 0 ? (
          <MenuItem disabled>No matches</MenuItem>
        ) : (
          filteredOptions.map((option) => (
            <MenuItem key={String(option.value)} value={option.value}>
              <Tooltip title={option.label} enterDelay={400} placement="right">
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    width: "100%",
                  }}
                >
                  {option.label}
                </span>
              </Tooltip>
            </MenuItem>
          ))
        )}
      </Select>
    </span>
  );
};

export default FilterHeaderDropdown;
